'use client'
import { FC, useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'next/navigation'
import { EmailIconBlue } from '@/icons/EmailIconBlue'
import { useGetTranslations } from '@/root/shared-hooks'
import { RecoveryEmailForm } from '@/root/modules/tuuleliinid/LoginPage/components/RecoveryEmailForm/RecoveryEmailForm'
import { ResetPasswordForm } from '@/root/modules/tuuleliinid/LoginPage/components/ResetPasswordForm/ResetPasswordForm'
import { Banner, ThreeDotsLoader } from '@/root/ui/tuuleliinid'
import { passwordResetValidate } from '@/root/shared-api/auth'

type RecoveryStep = 'form' | 'sent' | 'validating' | 'reset' | 'token-invalid'

export const RecoverySection: FC = () => {
  const { getTranslation } = useGetTranslations()
  const searchParams = useSearchParams()
  const token = searchParams.get('hash')
  const [step, setStep] = useState<RecoveryStep>(token ? 'validating' : 'form')
  const hasValidated = useRef(false)

  useEffect(() => {
    if (!token || hasValidated.current) return
    hasValidated.current = true

    window.history.replaceState({}, '', window.location.pathname)

    passwordResetValidate(token)
      .then(() => setStep('reset'))
      .catch(() => setStep('token-invalid'))
  }, [token])

  return (
    <div className="flex w-full flex-col items-center gap-6">
      {step !== 'validating' && <EmailIconBlue width={40} height={40} />}

      {step === 'form' && (
        <>
          <h2 className="text-primary-base text-xl font-bold">
            {getTranslation('recoveryForgotPasswordQuestion')}
          </h2>
          <p className="text-neutral-darkest max-w-sm text-center text-sm">
            {getTranslation('recoveryDescription')}
          </p>
          <RecoveryEmailForm onSubmit={() => setStep('sent')} />
        </>
      )}

      {step === 'sent' && (
        <>
          <h2 className="text-primary-base text-xl font-bold">
            {getTranslation('recoveryForgotPasswordQuestion')}
          </h2>
          <Banner variant="info" align="left" body={getTranslation('recoveryEmailSentMessage')} />
        </>
      )}

      {step === 'validating' && (
        <div className="flex min-h-88 flex-col items-center justify-center gap-4">
          <ThreeDotsLoader />
          <p className="text-neutral-darkest text-sm">{getTranslation('recoveryValidatingToken')}</p>
        </div>
      )}

      {step === 'reset' && token && (
        <>
          <h2 className="text-primary-base text-xl font-bold">
            {getTranslation('recoveryNewPasswordTitle')}
          </h2>
          <ResetPasswordForm token={token} />
        </>
      )}

      {step === 'token-invalid' && (
        <>
          <h2 className="text-primary-base text-xl font-bold">
            {getTranslation('recoveryForgotPasswordQuestion')}
          </h2>
          <Banner variant="error" align="left" body={getTranslation('recoveryTokenInvalid')} />
        </>
      )}
    </div>
  )
}
